import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import ModalVideo from "react-modal-video";
import TinySlider from "tiny-slider-react";
import moment from "moment";
import { useTranslation } from "react-i18next";

import Navlight from "./navlight";
import Switcher from "./switcher";
import { useAppDispatch } from "../redux/hook";
import { fetchNewsEdit } from "../redux/actions/newsAction";
import { getVideoId } from "../utils/utils";
import News from "./news";

import "../../node_modules/tiny-slider/dist/tiny-slider.css";

const settings = {
  container: ".tiny-one-item",
  items: 1,
  controls: true,
  mouseDrag: true,
  loop: true,
  rewind: true,
  autoplay: true,
  autoplayButtonOutput: false,
  autoplayTimeout: 3000,
  navPosition: "bottom",
  controlsText: [
    '<i class="mdi mdi-chevron-left "></i>',
    '<i class="mdi mdi-chevron-right"></i>',
  ],
  nav: false,
  speed: 400,
  gutter: 0,
};

export default function NewsDetail({ id }) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [newsData, setNewsData] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id > 0) {
      const loadNews = async () => {
        const news = await dispatch(fetchNewsEdit({ id }));
        if (typeof news.payload === "string" || news.payload === undefined) {
          setLoading(false);
        } else {
          setLoading(false);
          setNewsData(news.payload);
        }
      };
      loadNews();
    } else {
      setLoading(false);
    }
  }, [dispatch, id]);

  const [isOpen, setOpen] = useState(false);

  const image = [
    newsData?.image_file_02,
    newsData?.image_file_03,
    newsData?.image_file_04,
  ];

  const video = useCallback(() => {
    if (newsData.video !== undefined) {
      return (
        <ModalVideo
          channel="youtube"
          youtube={{ mute: 0, autoplay: 0 }}
          isOpen={isOpen}
          videoId={getVideoId(newsData.video)}
          onClose={() => setOpen(false)}
        />
      );
    }
  }, [newsData.video, isOpen]);

  if (loading) {
    return (
      <Box
        sx={{
          justifyContent: "center",
          alignItems: "center",
          display: "flex",
          top: "50%",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return id > 0 ? (
    <>
      <Navlight />

      <div className="relative">
        <div className="shape absolute sm:-bottom-px -bottom-[2px] start-0 end-0 overflow-hidden z-1 text-white dark:text-slate-900">
          <svg
            className="w-full h-auto scale-[2.0] origin-top"
            viewBox="0 0 2880 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0 48H1437.5H2880V0H2160C1442.5 52 720 0 720 0H0V48Z"
              fill="currentColor"
            ></path>
          </svg>
        </div>
      </div>
      <section className="relative md:py-24 py-16">
        <div className="container relative">
          <div className="grid grid-cols-1 relative">
            <div className="tiny-one-item">
              <TinySlider settings={settings}>
                {image.map((item, index) => {
                  return (
                    <div className="tiny-slide" key={index}>
                      <div className="m-2">
                        <img
                          src={
                            process.env.REACT_APP_API_URL +
                            "/wwwroot/images/" +
                            item
                          }
                          className="rounded-xl shadow dark:shadow-gray-800"
                          alt=""
                        />
                      </div>
                    </div>
                  );
                })}
              </TinySlider>
            </div>
          </div>
        </div>

        <div className="container relative md:mt-24 mt-16">
          <div className="grid md:grid-cols-12 grid-cols-1 gap-6">
            <div className="lg:col-span-4 md:col-span-5">
              <div className="bg-gray-50 dark:bg-slate-800 shadow dark:shadow-gray-800 p-6 rounded-xl">
                <h5 className="text-lg font-semibold border-b border-gray-100 dark:border-gray-700 pb-3 mb-3">
                  {t("external.news.news_info")} :
                </h5>
                <dl className="grid grid-cols-12 mb-0">
                  <dt className="md:col-span-4 col-span-5 mt-2">
                    {t("external.news.redactor")} :
                  </dt>
                  <dd className="md:col-span-8 col-span-7 mt-2 text-slate-400">
                    {newsData?.redactor}
                  </dd>

                  <dt className="md:col-span-4 col-span-5 mt-2">
                    {t("external.news.category")} :
                  </dt>
                  <dd className="md:col-span-8 col-span-7 mt-2 text-slate-400">
                    {newsData?.category}
                  </dd>

                  <dt className="md:col-span-4 col-span-5 mt-2">
                    {t("external.news.date")} :
                  </dt>
                  <dd className="md:col-span-8 col-span-7 mt-2 text-slate-400">
                    {moment(newsData?.date).format("DD/MM/YYYY")}
                  </dd>
                </dl>
              </div>
            </div>

            <div className="lg:col-span-8 md:col-span-7">
              <div className="lg:me-5">
                <h4 className="text-xl font-semibold mb-3 border-b border-gray-100 dark:border-gray-700 pb-3">
                  News :
                </h4>
                <p className="text-slate-400 dark:text-white/60">
                  {newsData?.text}
                </p>
              </div>
            </div>
          </div>

          <div className="md:flex justify-center mt-16">
            <div className="lg:w-full">
              <div className="relative">
                <img
                  src={
                    process.env.REACT_APP_API_URL +
                    "/wwwroot/images/" +
                    newsData?.image_file
                  }
                  className="rounded-xl shadow-md dark:shadow-gray-800"
                  alt=""
                />
                <div className="absolute bottom-2/4 translate-y-2/4 start-0 end-0 text-center">
                  <Link
                    to="#!"
                    onClick={() => setOpen(true)}
                    className="lightbox h-20 w-20 rounded-full shadow-lg shadow-slate-100 dark:shadow-slate-800 inline-flex items-center justify-center bg-white dark:bg-slate-900 text-orange-600"
                  >
                    <i className="mdi mdi-play inline-flex items-center justify-center text-2xl"></i>
                  </Link>
                </div>
                {video()}
              </div>
            </div>
          </div>
        </div>
      </section>
      <Switcher />
    </>
  ) : (
    <News />
  );
}
